import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  FileText, 
  Calendar,
  ArrowDownRight,
  ArrowUpRight,
  RefreshCw,
  TrendingUp,
  Wallet,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function UserStatement() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Get month name based on current language
  const getMonthName = (monthNum) => {
    const date = new Date(2024, monthNum - 1, 1);
    return date.toLocaleString(i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB', { month: 'long' });
  };

  // Get type config with translations
  const getTypeConfig = () => ({
    deposit: { label: t('statement.deposit'), color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: ArrowDownRight },
    withdrawal: { label: t('statement.withdrawal'), color: 'text-red-400', bg: 'bg-red-500/10', icon: ArrowUpRight },
    utility_payment: { label: t('statement.utilityPayment'), color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Wallet },
    reinvestment: { label: t('statement.reinvestment'), color: 'text-purple-400', bg: 'bg-purple-500/10', icon: RefreshCw },
    adjustment: { label: t('statement.adjustment'), color: 'text-slate-400', bg: 'bg-slate-500/10', icon: TrendingUp },
    credit: { label: t('statement.credit'), color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: ArrowDownRight },
    debit: { label: t('statement.debit'), color: 'text-red-400', bg: 'bg-red-500/10', icon: ArrowUpRight },
    profit: { label: t('statement.profit'), color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: TrendingUp }
  });

  useEffect(() => {
    fetchStatement();
  }, [year, month, page]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/statement/monthly?year=${year}&month=${month}&page=${page}&limit=20`);
      setData(response.data);
    } catch (error) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    setPage(1);
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    setPage(1);
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const formatCurrency = (value) => {
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatDate = (dateStr) => {
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB';
    return new Date(dateStr).toLocaleString(locale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const TYPE_CONFIG = getTypeConfig();
  const pagination = data?.pagination || {};

  return (
    <div className="animate-fade-in" data-testid="user-statement-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('statement.title')}</h1>
        <p className="text-slate-400 text-sm">{t('statement.subtitle')}</p>
      </div>

      {/* Month Selector */}
      <div className="glass-card p-4 mb-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goToPreviousMonth}
            className="text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-blue-400" />
            <span className="text-xl font-semibold text-white capitalize">
              {getMonthName(month)} {year}
            </span>
          </div>
          
          <Button
            variant="ghost"
            onClick={goToNextMonth}
            className="text-slate-400 hover:text-white"
            disabled={month === now.getMonth() + 1 && year === now.getFullYear()}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <ArrowDownRight className="w-4 h-4" />
                <span className="text-xs">{t('statement.deposits')}</span>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(data?.summary?.total_deposits || 0)}</p>
            </div>
            
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <ArrowUpRight className="w-4 h-4" />
                <span className="text-xs">{t('statement.withdrawals')}</span>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(data?.summary?.total_withdrawals || 0)}</p>
            </div>
            
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Wallet className="w-4 h-4" />
                <span className="text-xs">{t('statement.payments')}</span>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(data?.summary?.total_utility_payments || 0)}</p>
            </div>
            
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 text-purple-400 mb-2">
                <RefreshCw className="w-4 h-4" />
                <span className="text-xs">{t('statement.reinvestments')}</span>
              </div>
              <p className="text-lg font-bold text-white">{formatCurrency(data?.summary?.total_reinvestments || 0)}</p>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="glass-card overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  <h2 className="text-lg font-semibold text-white">{t('statement.transactions')}</h2>
                  <span className="text-sm text-slate-400">({data?.summary?.transaction_count || 0})</span>
                </div>
              </div>
            </div>

            {data?.transactions?.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-white/5 text-left">
                        <th className="p-4 text-xs text-slate-400 font-medium">{t('statement.date')}</th>
                        <th className="p-4 text-xs text-slate-400 font-medium">{t('statement.type')}</th>
                        <th className="p-4 text-xs text-slate-400 font-medium text-right">{t('statement.value')}</th>
                        <th className="p-4 text-xs text-slate-400 font-medium">{t('statement.description')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {data.transactions.map((transaction) => {
                        const config = TYPE_CONFIG[transaction.type] || TYPE_CONFIG.adjustment;
                        const IconComponent = config.icon;
                        const isNegative = ['withdrawal', 'debit'].includes(transaction.type);
                        return (
                          <tr key={transaction.id} className="hover:bg-white/5 transition-colors">
                            <td className="p-4 text-sm text-slate-300">
                              {formatDate(transaction.created_at)}
                            </td>
                            <td className="p-4">
                              <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-lg ${config.bg}`}>
                                <IconComponent className={`w-3 h-3 ${config.color}`} />
                                <span className={`text-xs ${config.color}`}>{config.label}</span>
                              </div>
                            </td>
                            <td className={`p-4 text-sm font-semibold text-right ${isNegative ? 'text-red-400' : 'text-emerald-400'}`}>
                              {isNegative ? '-' : '+'}{formatCurrency(transaction.amount)}
                            </td>
                            <td className="p-4 text-sm text-slate-400 max-w-xs truncate">
                              {transaction.description}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-white/5">
                  {data.transactions.map((transaction) => {
                    const config = TYPE_CONFIG[transaction.type] || TYPE_CONFIG.adjustment;
                    const IconComponent = config.icon;
                    const isNegative = ['withdrawal', 'debit'].includes(transaction.type);
                    return (
                      <div key={transaction.id} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-lg ${config.bg}`}>
                            <IconComponent className={`w-3 h-3 ${config.color}`} />
                            <span className={`text-xs ${config.color}`}>{config.label}</span>
                          </div>
                          <span className={`font-semibold ${isNegative ? 'text-red-400' : 'text-emerald-400'}`}>
                            {isNegative ? '-' : '+'}{formatCurrency(transaction.amount)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">{formatDate(transaction.created_at)}</p>
                        <p className="text-sm text-slate-400 mt-1 truncate">{transaction.description}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="p-4 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-400">
                        {t('statement.page')} {pagination.page} {t('statement.of')} {pagination.total_pages}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(1)}
                          disabled={!pagination.has_prev}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                        >
                          <ChevronsLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(page - 1)}
                          disabled={!pagination.has_prev}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-white px-3">{pagination.page}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(page + 1)}
                          disabled={!pagination.has_next}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage(pagination.total_pages)}
                          disabled={!pagination.has_next}
                          className="text-slate-400 hover:text-white disabled:opacity-30"
                        >
                          <ChevronsRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">{t('statement.noMovements')}</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
